import './cartDrawer.scss';

/* import libraries */
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Badge, IconButton, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import { usePathname, useRouter } from 'next/navigation';
import * as React from 'react';

/* import interfaces */
import { CartItem } from '@/components/cart/components/cartInterfaces';

/* import enums */
import orderType from '@/constants/enums/orderType';

/* import context */
import { useShoppingCart } from '../../../context/ShoppingCartContext';
/* import components */
import CartProduct from '../components/cartProduct';

/* import services */
import { MixpanelEvent } from '@/services/eventEnums';
import { MixpanelTracking } from '@/services/mixpanel';
import { priceAfterTax, priceNumberFormat } from '@/lib/utils';
import { calculateExistingItemPrice } from '@/helper/cartHelper';
import { useTranslation } from 'react-i18next';
import NewCartProduct from '../components/newCartProduct';

interface CartDrawerProps {
  path: string;
}

export default function CartDrawer({ path }: CartDrawerProps) {
  const { cartQuantity, cartItems, setIsNew, isOpen, setIsOpen, tax, resetCart } =
    useShoppingCart();
  // const isArchiveNewDesignEnabled = posthog.isFeatureEnabled('cart_drawer_and_cart_new_design');
  const isArchiveNewDesignEnabled = true;
  const router = useRouter();
  const pathname = usePathname();
  const price = calculateExistingItemPrice(cartItems) || 0;
  const { t } = useTranslation('drawer');

  const toggleDrawer = (newOpen: boolean) => () => {
    if (newOpen) {
      const totalPrice = cartItems?.reduce((acc, item) => acc + (item.cardData?.price || 0), 0);

      const existingItemsId = cartItems
        ?.filter(item => item?.type === orderType.EXISTING)
        .map(item => item?.id);
      const newItemsId = cartItems
        ?.filter(item => item?.type === orderType.NEW)
        .map(item => item?.id);
      const results = [...(existingItemsId || []), ...(newItemsId || [])];

      MixpanelTracking.getInstance().track(MixpanelEvent.CART_CLICK, {
        results,
        number_of_items: cartQuantity,
        total_price: priceNumberFormat(totalPrice),
      });
    }

    setIsOpen(newOpen);
  };

  const goToCart = () => {
    MixpanelTracking.getInstance().track(MixpanelEvent.CART_BEGIN_CHECKOUT, {
      item_ids: cartItems?.map(data => data?.id),
      number_of_items: cartQuantity,
      total_price: `${priceAfterTax(price, tax)}`,
    });

    router.push('/cart');
    setIsOpen(false);
  };

  const addAdditionalImages = () => {
    if (pathname !== '/') router.push('/');
    setIsOpen(false);
  };
  const deleteAll = () => {
    resetCart();
  };

  const newItems: CartItem[] = cartItems?.filter(item => item?.type === orderType.NEW) || [];
  const existingItems: CartItem[] =
    cartItems?.filter(item => item?.type === orderType.EXISTING) || [];

  const DrawerList = (
    <div>
      <Box className="drawer-header">
        <Typography variant="h5">{t('cartTitle')}</Typography>
        <IconButton onClick={toggleDrawer(false)} name="Close Cart">
          <CloseIcon aria-label="close-cart-icon" />
        </IconButton>
      </Box>
      <Box className="drawer-content cart-drawer-content">
        {cartQuantity === 0 && (
          <>
            <Typography variant="body2">{t('cartEmpty')}</Typography>
            <Button
              variant="contained"
              fullWidth
              name="order-existing-image-button"
              onClick={() => addAdditionalImages()}
            >
              {t('addImages')}
            </Button>
          </>
        )}

        {newItems.length > 0 && (
          <>
            <Typography className="product-title" variant="body2">
              {t('newImages')}
            </Typography>
            {newItems.map(item => (
              <CartProduct
                id={item?.id}
                key={item?.id}
                type={item?.type as orderType}
                cardData={item?.cardData}
              />
            ))}
          </>
        )}

        {existingItems.length > 0 && (
          <>
            <Typography className="product-title" variant="body2">
              {t('existingImages')}
            </Typography>
            {existingItems.map(item => {
              if (isArchiveNewDesignEnabled && item?.cardData?.cartDrawerElements) {
                return (
                  <NewCartProduct
                    key={item?.id}
                    id={item?.id}
                    type={item?.type as orderType}
                    cardData={item?.cardData}
                    overlap={item?.overlap ? item.overlap : null}
                    overlapData={item?.overlapData ? item.overlapData : []}
                  />
                );
              } else {
                return (
                  <CartProduct
                    key={item?.id}
                    id={item?.id}
                    type={item?.type as orderType}
                    cardData={item?.cardData}
                    overlap={item?.overlap ? item.overlap : null}
                    overlapData={item?.overlapData ? item.overlapData : []}
                  />
                );
              }
            })}
          </>
        )}
      </Box>
      {cartItems?.length > 0 && (
        <Box
          className="drawer-footer cart-drawer-footer"
          display="flex"
          justifyContent="space-between"
          gap={1}
        >
          <Button
            aria-label="clear-cart-btn"
            onClick={() => deleteAll()}
            variant="outlined"
            size="medium"
          >
            {t('clearCart')}
          </Button>
          <Button
            onClick={goToCart}
            variant="contained"
            size="large"
            startIcon={<ShoppingCartOutlinedIcon />}
            name="begin-checkout-button"
          >
            {t('beginCheckout')}
          </Button>
        </Box>
      )}
    </div>
  );
  return (
    <Box>
      <IconButton
        onClick={toggleDrawer(true)}
        className={['/cart', '/fr-CA/cart'].includes(path) ? 'active' : ''}
        name="Cart Icon"
        aria-label="cart-icon-btn"
      >
        <Badge
          color="secondary"
          badgeContent={cartQuantity}
          showZero
          aria-label="cart-item-quantity-badge"
        >
          <ShoppingCartOutlinedIcon aria-label="cart-icon" />
        </Badge>
      </IconButton>
      <Drawer
        ModalProps={{
          className: 'skywatch-drawer-wrapper',
        }}
        PaperProps={{ className: 'skywatch-drawer' }}
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
        aria-label="cart-drawer-modal"
      >
        {DrawerList}
      </Drawer>
    </Box>
  );
}
